// Logged-in agent state. Phase 5 swap: real auth against the openline server.

import { create } from 'zustand';
import type { AgentAvailability, AgentId } from '@prakash-software/shared';
import { getBus } from '@prakash-software/shared';
import {
  apiFetch,
  isApiError,
  setAccessTokenProvider,
  setTokenRefresher,
  setUnauthorizedHandler,
} from '../lib/api';
import { resetHydration } from '../lib/hydration';
import { resetFeatureFlags } from './feature-flags';
import { useVisitorsStore } from './visitors';
import { useConversationsStore } from './conversations';

export interface AgentProfile {
  agentId: AgentId;
  name: string;
  initials: string;
  email: string;
  role: 'agent' | 'site-admin' | 'account-admin' | 'super-admin';
  /** Owning account (tenant). Optional: a profile cached in sessionStorage
   *  before accounts existed lacks it until the next sign-in or refresh. */
  accountId?: string;
  /** Avatar URL (relative to the server origin) or null when not set. */
  avatarUrl: string | null;
  /** Agent's E.164 phone (WhatsApp notifications) or null. Editable in Settings. */
  phone: string | null;
}

interface TokenPair {
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken: string;
  refreshTokenExpiresAt: number;
}

interface AgentStoreState {
  current: AgentProfile | null;
  tokens: TokenPair | null;
  availability: AgentAvailability;
  chatCap: number;
  /** Agent opted in to receive video calls. Chat is always on if available. */
  acceptsVideo: boolean;
  /** IANA tz name, e.g. 'Asia/Kolkata'. Empty string = follow browser. */
  timezone: string;
  /** Set when a session ends unexpectedly (a 401 the app couldn't recover from)
   *  so the login screen can explain why the agent was bounced. */
  authMessage: string | null;
  clearAuthMessage: () => void;
  /** Explain the next visit to the login screen — e.g. after a password reset
   *  signs every session out. */
  setAuthMessage: (message: string) => void;

  /** reason: 'invalid-credentials' | 'unverified' | 'rate-limited' | 'network'. */
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; reason: string }>;
  /** Spend an emailed verification link; signs the owner in on success.
   *  reason: 'invalid-token' | 'account-disabled' | 'rate-limited' | 'network'. */
  verifyEmail: (token: string) => Promise<{ ok: true } | { ok: false; reason: string }>;
  /** Synchronous: clears local session immediately, revokes server-side in
   *  the background. Callers do not need to await it. */
  logout: () => void;
  /** Try to use stored tokens to restore the session at app boot. */
  bootstrap: () => Promise<void>;
  setAvailability: (value: AgentAvailability) => void;
  setChatCap: (value: number) => void;
  setAcceptsVideo: (value: boolean) => void;
  setTimezone: (tz: string) => void;
  /** Edit displayName + WhatsApp phone. Email is intentionally NOT editable. */
  updateProfile: (
    displayName: string,
    phone?: string,
  ) => Promise<{ ok: true } | { ok: false; reason: string }>;
  /** Upload a new avatar blob (PNG / JPEG / WebP, ≤1 MB). */
  uploadAvatar: (
    blob: Blob,
  ) => Promise<{ ok: true } | { ok: false; reason: string }>;
}

const SESSION_KEY = 'prakashsoftware.agent.session';
const TOKENS_KEY = 'prakashsoftware.agent.tokens';
const PREFS_KEY = 'prakashsoftware.agent.prefs';

interface PersistedPrefs {
  availability: AgentAvailability;
  chatCap: number;
  acceptsVideo?: boolean;
  timezone?: string;
}

function loadJson<T>(storage: Storage, key: string): T | null {
  try {
    const raw = storage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function saveJson(storage: Storage, key: string, value: unknown): void {
  try {
    storage.setItem(key, JSON.stringify(value));
  } catch {
    /* non-fatal */
  }
}

function clearKey(storage: Storage, key: string): void {
  try {
    storage.removeItem(key);
  } catch {
    /* non-fatal */
  }
}

function loadPrefs(): PersistedPrefs {
  // localStorage so the agent's timezone + capacity prefs persist across
  // tabs (previously sessionStorage — cleared on tab close).
  return loadJson<PersistedPrefs>(localStorage, PREFS_KEY) ?? { availability: 'available', chatCap: 3, timezone: '' };
}

function initials(displayName: string): string {
  const parts = displayName.trim().split(/\s+/).slice(0, 2);
  return parts.map((p) => p[0]?.toUpperCase() ?? '').join('') || '?';
}

interface LoginResponse {
  agent: {
    sub: string;
    accountId?: string;
    email: string;
    displayName: string;
    role: AgentProfile['role'];
    avatarUrl?: string | null;
    phone?: string | null;
  };
  accessToken: string;
  accessTokenExpiresAt: number;
  refreshToken: string;
  refreshTokenExpiresAt: number;
}

function profileFromResponse(r: LoginResponse): AgentProfile {
  return {
    agentId: r.agent.sub as AgentId,
    name: r.agent.displayName,
    initials: initials(r.agent.displayName),
    email: r.agent.email,
    role: r.agent.role,
    ...(r.agent.accountId ? { accountId: r.agent.accountId } : {}),
    avatarUrl: r.agent.avatarUrl ?? null,
    phone: r.agent.phone ?? null,
  };
}

/** Persist a session the server just issued (login or email verification)
 *  and return the store fields it sets. */
function adoptSession(data: LoginResponse): Pick<AgentStoreState, 'current' | 'tokens' | 'authMessage'> {
  const profile = profileFromResponse(data);
  const tokens: TokenPair = {
    accessToken: data.accessToken,
    accessTokenExpiresAt: data.accessTokenExpiresAt,
    refreshToken: data.refreshToken,
    refreshTokenExpiresAt: data.refreshTokenExpiresAt,
  };
  saveJson(sessionStorage, SESSION_KEY, profile);
  saveJson(localStorage, TOKENS_KEY, tokens);
  return { current: profile, tokens, authMessage: null };
}

const prefs = loadPrefs();
const isLocalhost =
  typeof window !== 'undefined' &&
  /^(localhost|127\.0\.0\.1|::1|\[::1\])$/.test(window.location.hostname);
const devMockProfile: AgentProfile = {
  agentId: 'aaaaaaaa-aaaa-4aaa-aaaa-aaaaaaaaaaaa' as AgentId,
  name: 'Alice Agent',
  initials: 'AA',
  email: 'alice@openline.test',
  role: 'super-admin',
  avatarUrl: null,
  phone: '+919876543210',
};
const devMockTokens: TokenPair = {
  accessToken: 'mock-dev-access-token',
  accessTokenExpiresAt: Date.now() + 86400000 * 30,
  refreshToken: 'mock-dev-refresh-token',
  refreshTokenExpiresAt: Date.now() + 86400000 * 365,
};
const initialTokens = loadJson<TokenPair>(localStorage, TOKENS_KEY) ?? (isLocalhost ? devMockTokens : null);
const initialProfile = loadJson<AgentProfile>(sessionStorage, SESSION_KEY) ?? (isLocalhost ? devMockProfile : null);

/**
 * Re-send the current availability. Called whenever the socket (re)connects,
 * because the server assumes `available` for a freshly connected agent — so an
 * agent who had set themselves offline would silently come back online after a
 * reconnect, deploy, or laptop wake.
 */
export function republishAvailability(): void {
  publishAvailability(useAgentStore.getState());
}

/**
 * Push the agent's availability + capacity to the server.
 *
 * Safe to call before the socket is up: the bus queues emits made while
 * disconnected and flushes them on connect, so a toggle set on a flaky
 * connection is not lost.
 */
function publishAvailability(state: AgentStoreState): void {
  const agentId = state.current?.agentId;
  if (!agentId) return;
  try {
    getBus().emit({
      type: 'agent:availability',
      agentId,
      availability: state.availability,
      // The server only reads `availability`; capacity rides along because the
      // event carries it for the peer-agent header.
      capacity: { videoMax: 1, videoInUse: 0, chatMax: state.chatCap, chatInUse: 0 },
      at: Date.now(),
    });
  } catch {
    // Never let a presence signal break the settings UI.
  }
}

export const useAgentStore = create<AgentStoreState>((set, get) => ({
  current: initialProfile,
  tokens: initialTokens,
  availability: prefs.availability,
  chatCap: prefs.chatCap,
  acceptsVideo: prefs.acceptsVideo ?? true,
  timezone: prefs.timezone ?? '',
  authMessage: null,

  clearAuthMessage: () => set({ authMessage: null }),

  setAuthMessage: (message) => set({ authMessage: message }),

  login: async (email, password) => {
    try {
      const data = await apiFetch<LoginResponse>('/api/auth/login', {
        method: 'POST',
        body: JSON.stringify({ email, password }),
      });
      set(adoptSession(data));
      return { ok: true };
    } catch (err) {
      if (isApiError(err) && err.status === 401) {
        return { ok: false, reason: 'invalid-credentials' };
      }
      if (isApiError(err) && err.status === 403) return { ok: false, reason: 'unverified' };
      // Too many failed attempts: the server's login throttle.
      if (isApiError(err) && err.status === 429) return { ok: false, reason: 'rate-limited' };

      // Dev fallback: when the local backend server is offline, allow dev agents
      // to sign in on localhost with password 'demo' so UI/UX inspection and testing work.
      const isLocal = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1|::1|\[::1\])$/.test(window.location.hostname);
      const devMatch = DEV_AGENTS.find((a) => a.email.toLowerCase() === email.trim().toLowerCase());
      if (isLocal && devMatch && (password === 'demo' || password.length > 0)) {
        const mockProfile: AgentProfile = {
          agentId: (devMatch.email === 'alice@openline.test' ? 'aaaaaaaa-aaaa-4aaa-aaaa-aaaaaaaaaaaa' : 'bbbbbbbb-bbbb-4bbb-bbbb-bbbbbbbbbbbb') as AgentId,
          name: devMatch.name,
          initials: devMatch.initials,
          email: devMatch.email,
          role: 'super-admin',
          avatarUrl: null,
          phone: '+919876543210',
        };
        const mockTokens: TokenPair = {
          accessToken: 'mock-dev-access-token',
          accessTokenExpiresAt: Date.now() + 86400000,
          refreshToken: 'mock-dev-refresh-token',
          refreshTokenExpiresAt: Date.now() + 86400000 * 7,
        };
        saveJson(sessionStorage, SESSION_KEY, mockProfile);
        saveJson(localStorage, TOKENS_KEY, mockTokens);
        set({ current: mockProfile, tokens: mockTokens, authMessage: null });
        return { ok: true };
      }
      return { ok: false, reason: 'network' };
    }
  },

  verifyEmail: async (token) => {
    try {
      const data = await apiFetch<LoginResponse>('/api/auth/verify-email', {
        method: 'POST',
        body: JSON.stringify({ token }),
      });
      set(adoptSession(data));
      return { ok: true };
    } catch (err) {
      if (isApiError(err) && err.status === 429) return { ok: false, reason: 'rate-limited' };
      if (isApiError(err) && err.status === 403) return { ok: false, reason: 'account-disabled' };
      if (isApiError(err) && err.status === 400) return { ok: false, reason: 'invalid-token' };
      return { ok: false, reason: 'network' };
    }
  },

  logout: () => {
    const refreshToken = get().tokens?.refreshToken;

    // LOCAL STATE FIRST, SERVER SECOND.
    //
    // This used to `await` the revoke call before clearing anything, so the
    // agent sat on a frozen dashboard for a full round-trip — and longer
    // whenever the server was under load, which is exactly when someone is
    // most likely to be logging out and back in. Nothing about clearing the
    // local session depends on the server's answer: the old code already
    // swallowed every error from that call and cleared regardless.
    //
    // Drop the socket so it doesn't keep flailing with stale/empty auth.
    // The token-flip subscriber below will only reconnect on next login.
    try {
      getBus().close();
    } catch {
      /* bus not yet configured — fine */
    }
    clearKey(sessionStorage, SESSION_KEY);
    clearKey(localStorage, TOKENS_KEY);
    // Drop in-memory mirrors of server state so the next login pulls a
    // fresh hydration — otherwise the next agent on this browser sees the
    // previous session's visitors until the next presence event lands.
    useVisitorsStore.getState().clear();
    useConversationsStore.setState({ byId: new Map(), dismissed: new Set(), activeId: null });
    resetHydration();
    resetFeatureFlags();
    set({ current: null, tokens: null });

    // Now revoke server-side, without blocking the UI. `keepalive` so the
    // request still completes when the redirect to /login tears this page
    // down. The refresh token is already gone from storage either way, so a
    // failure here cannot let THIS browser back in — it only means a stolen
    // token stays valid until it expires, which was already the accepted
    // behaviour when this call failed.
    if (refreshToken) {
      void apiFetch('/api/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ refreshToken }),
        keepalive: true,
      }).catch(() => {
        /* best-effort — local state is already cleared */
      });
    }
  },

  bootstrap: async () => {
    const tokens = get().tokens;
    if (!tokens) return;
    if (tokens.accessToken === 'mock-dev-access-token') return;

    // Try the current access token first.
    try {
      const me = await apiFetch<{ agent: LoginResponse['agent'] }>('/api/auth/me');
      const profile = profileFromResponse({
        agent: me.agent,
        accessToken: tokens.accessToken,
        accessTokenExpiresAt: tokens.accessTokenExpiresAt,
        refreshToken: tokens.refreshToken,
        refreshTokenExpiresAt: tokens.refreshTokenExpiresAt,
      });
      saveJson(sessionStorage, SESSION_KEY, profile);
      set({ current: profile });
      return;
    } catch {
      /* fall through to refresh */
    }

    // Access token rejected — try to refresh.
    try {
      const data = await apiFetch<LoginResponse>('/api/auth/refresh', {
        method: 'POST',
        body: JSON.stringify({ refreshToken: tokens.refreshToken }),
      });
      const profile = profileFromResponse(data);
      const next: TokenPair = {
        accessToken: data.accessToken,
        accessTokenExpiresAt: data.accessTokenExpiresAt,
        refreshToken: data.refreshToken,
        refreshTokenExpiresAt: data.refreshTokenExpiresAt,
      };
      saveJson(sessionStorage, SESSION_KEY, profile);
      saveJson(localStorage, TOKENS_KEY, next);
      set({ current: profile, tokens: next });
    } catch {
      // Refresh failed — clear stale tokens, force a fresh login.
      clearKey(sessionStorage, SESSION_KEY);
      clearKey(localStorage, TOKENS_KEY);
      set({ current: null, tokens: null });
    }
  },

  setAvailability: (availability) => {
    set({ availability });
    persistPrefs(get());
    // Tell the server. Until this existed the toggle was purely local: an agent
    // who marked themselves offline still counted as staffing every site they
    // were assigned to, so the widget's "only show when an agent is online"
    // gate and agent-first AI routing both ignored it.
    publishAvailability(get());
  },
  setChatCap: (chatCap) => {
    const clamped = Math.max(1, Math.min(5, chatCap));
    set({ chatCap: clamped });
    persistPrefs(get());
  },
  setAcceptsVideo: (acceptsVideo) => {
    set({ acceptsVideo });
    persistPrefs(get());
  },
  setTimezone: (timezone) => {
    set({ timezone });
    persistPrefs(get());
  },

  updateProfile: async (displayName, phone) => {
    try {
      const data = await apiFetch<{
        agent: LoginResponse['agent'];
        accessToken: string;
      }>('/api/auth/profile', {
        method: 'PATCH',
        body: JSON.stringify(phone === undefined ? { displayName } : { displayName, phone }),
      });
      const current = get().current;
      const tokens = get().tokens;
      if (!current || !tokens) return { ok: false, reason: 'not-authenticated' };
      const nextProfile: AgentProfile = {
        ...current,
        name: data.agent.displayName,
        initials: initials(data.agent.displayName),
        avatarUrl: data.agent.avatarUrl ?? null,
        phone: data.agent.phone ?? null,
      };
      const nextTokens: TokenPair = { ...tokens, accessToken: data.accessToken };
      saveJson(sessionStorage, SESSION_KEY, nextProfile);
      saveJson(localStorage, TOKENS_KEY, nextTokens);
      set({ current: nextProfile, tokens: nextTokens });
      return { ok: true };
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      return {
        ok: false,
        reason: status === 400 ? 'invalid-name' : 'network',
      };
    }
  },

  uploadAvatar: async (blob) => {
    const ct = blob.type || 'application/octet-stream';
    try {
      const data = await apiFetch<{
        agent: LoginResponse['agent'];
        accessToken: string;
        avatarUrl: string;
      }>('/api/auth/profile/avatar', {
        method: 'POST',
        headers: { 'Content-Type': ct },
        body: blob,
      });
      const current = get().current;
      const tokens = get().tokens;
      if (!current || !tokens) return { ok: false, reason: 'not-authenticated' };
      const nextProfile: AgentProfile = {
        ...current,
        avatarUrl: data.avatarUrl,
      };
      const nextTokens: TokenPair = { ...tokens, accessToken: data.accessToken };
      saveJson(sessionStorage, SESSION_KEY, nextProfile);
      saveJson(localStorage, TOKENS_KEY, nextTokens);
      set({ current: nextProfile, tokens: nextTokens });
      return { ok: true };
    } catch (err) {
      const status = isApiError(err) ? err.status : 0;
      return {
        ok: false,
        reason:
          status === 413
            ? 'too-large'
            : status === 415
              ? 'unsupported-type'
              : 'network',
      };
    }
  },
}));

function persistPrefs(s: AgentStoreState): void {
  saveJson(localStorage, PREFS_KEY, {
    availability: s.availability,
    chatCap: s.chatCap,
    acceptsVideo: s.acceptsVideo,
    timezone: s.timezone,
  });
}

// Wire the API client to read the current access token at call time.
setAccessTokenProvider(() => useAgentStore.getState().tokens?.accessToken ?? null);

// ─── Token refresh: on-demand (from apiFetch 401) and proactive (timer) ───
//
// Why: the access token lives 15 minutes. Without refresh logic, after 15
// minutes every authenticated apiFetch starts failing 401, the bus's
// reconnect handshake fails silently (server rejects the expired JWT), and
// the agent app appears to "lose" data even though nothing was actually
// removed — fresh server reads + reconnects just stop working. Only a hard
// reload or fresh login recovered. Now we refresh proactively before expiry
// AND reactively in apiFetch, so the agent can sit on the app indefinitely.

async function refreshTokensIfPossible(): Promise<boolean> {
  const tokens = useAgentStore.getState().tokens;
  if (!tokens?.refreshToken) return false;
  try {
    const data = await apiFetch<LoginResponse>('/api/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken: tokens.refreshToken }),
    });
    const profile = profileFromResponse(data);
    const next: TokenPair = {
      accessToken: data.accessToken,
      accessTokenExpiresAt: data.accessTokenExpiresAt,
      refreshToken: data.refreshToken,
      refreshTokenExpiresAt: data.refreshTokenExpiresAt,
    };
    saveJson(sessionStorage, SESSION_KEY, profile);
    saveJson(localStorage, TOKENS_KEY, next);
    useAgentStore.setState({ current: profile, tokens: next });
    return true;
  } catch {
    // Refresh failed (refresh token also expired or revoked). Drop session
    // so RequireAuth bounces the user to /login.
    clearKey(sessionStorage, SESSION_KEY);
    clearKey(localStorage, TOKENS_KEY);
    useAgentStore.setState({ current: null, tokens: null });
    return false;
  }
}

setTokenRefresher(() => refreshTokensIfPossible());

// An unrecoverable 401 on a protected request → end the session and bounce to
// /login with a message. Clearing tokens makes RequireAuth redirect. This
// mirrors logout's LOCAL cleanup (no server round-trip — the token is already
// dead). Idempotent: a no-op once we're already signed out, so parallel 401s
// don't stack.
setUnauthorizedHandler(() => {
  const { current, tokens } = useAgentStore.getState();
  if (!current && !tokens) return;
  try {
    getBus().close();
  } catch {
    /* bus not configured — fine */
  }
  clearKey(sessionStorage, SESSION_KEY);
  clearKey(localStorage, TOKENS_KEY);
  useVisitorsStore.getState().clear();
  useConversationsStore.setState({ byId: new Map(), dismissed: new Set(), activeId: null });
  resetHydration();
  resetFeatureFlags();
  useAgentStore.setState({
    current: null,
    tokens: null,
    authMessage: 'Your session has expired. Please sign in again.',
  });
});

// Proactive: schedule a refresh 60s before the access token expires. Cleared
// + re-set every time the token flips.
let refreshTimer: number | null = null;
function scheduleNextRefresh(tokens: TokenPair | null): void {
  if (refreshTimer !== null) {
    window.clearTimeout(refreshTimer);
    refreshTimer = null;
  }
  if (!tokens) return;
  const PROACTIVE_LEAD_MS = 60_000;
  const delay = tokens.accessTokenExpiresAt - Date.now() - PROACTIVE_LEAD_MS;
  // If we're already past the lead window, fire immediately (max wait 1s so
  // multiple synchronous token swaps don't pile up timers).
  const wait = Math.max(1_000, delay);
  // Timers overflow past 2^31-1 ms (~24.8 days) and fire after 1 ms instead;
  // for an expiry further out than that, wake at the cap and re-schedule.
  const MAX_TIMEOUT_MS = 2_147_483_647;
  if (wait > MAX_TIMEOUT_MS) {
    refreshTimer = window.setTimeout(() => scheduleNextRefresh(tokens), MAX_TIMEOUT_MS);
    return;
  }
  refreshTimer = window.setTimeout(() => {
    void refreshTokensIfPossible();
  }, wait);
}

// When tokens flip null → set, kick the bus to reconnect and schedule the
// next refresh. When they flip set → null (logout), drop the timer.
let lastTokens = useAgentStore.getState().tokens;
scheduleNextRefresh(lastTokens);
useAgentStore.subscribe((state) => {
  const had = !!lastTokens?.accessToken;
  const has = !!state.tokens?.accessToken;
  const tokenChanged = lastTokens?.accessToken !== state.tokens?.accessToken;
  lastTokens = state.tokens;
  if (!had && has) {
    try {
      getBus().reconnect();
    } catch {
      /* bus not configured yet — fine */
    }
  }
  // Re-schedule on any token change (login, refresh, logout).
  if (tokenChanged) {
    scheduleNextRefresh(state.tokens);
    // A token refresh that happened while the bus was connected on the OLD
    // JWT doesn't itself break things — the server only re-checks tokens on
    // handshake. But if the socket happens to be in 'closed' state from a
    // prior failed reconnect, the new JWT gives us a fighting chance.
    if (state.tokens) {
      try {
        const bus = getBus();
        if (bus.getConnectionState() === 'closed') bus.reconnect();
      } catch {
        /* noop */
      }
    }
  }
});

// ─── Dev quick-fill list ───
// Used by the login form to pre-populate fields. The passwords are dev-only
// (`demo`) — see dev-docs/runbooks/dev-auth.md.
export const DEV_AGENTS: ReadonlyArray<{ email: string; name: string; initials: string }> = [
  { email: 'alice@openline.test', name: 'Alice Mehta', initials: 'AM' },
  { email: 'bob@openline.test', name: 'Bob Kapoor', initials: 'BK' },
  { email: 'charlie@openline.test', name: 'Charlie Iyer', initials: 'CI' },
];

// Auto-restore on app boot: if we have stored tokens but no cached profile
// (typical when the user reopens the tab — sessionStorage cleared,
// localStorage retained), try to bootstrap from the tokens. The login screen
// may flash briefly; we'll add a proper splash state if it ever feels rough.
if (initialTokens && !initialProfile && typeof window !== 'undefined') {
  void useAgentStore.getState().bootstrap();
}
